import { CAPTURE_SAMPLE_RATE, CaptureAppPlayback, type SpeakerChannel } from './captureAppPlayback'

const CAPTURE_APP_WS_URL = 'ws://localhost:49521'
/** Capability of newer capture apps: on request, send the microphone and system audio as separate channels. */
const SOURCE_AUDIO_CAPABILITY = 'source-audio'
/** Max time to wait for the format message, which the app sends before any audio */
const FORMAT_TIMEOUT_MS = 3000

/**
 * Audio input source used for the current AI Teammate listening session.
 */
export type TeammateAudioSource = 'capture-app' | 'mic'

/**
 * The audio of one listening session.
 * `stream` is the mix of all audio: for ordinary transcription and for filling the playbook.
 * With speaker attribution, `speakerChannels` also contains the microphone and the system audio separately.
 * The browser microphone, older capture apps, and an app that captures only one input provide only `stream`.
 */
export interface TeammateAudioSourceResult {
  stream: MediaStream
  source: TeammateAudioSource
  /** One channel for `me` and one for `remote`, or empty when speaker channels are not available. */
  speakerChannels: SpeakerChannel[]
}

/**
 * Supplies audio from the Teammate capture app, or from the browser microphone when the app is unavailable.
 * The app sends audio samples over a local WebSocket. This controller owns that connection and its protocol.
 * {@link CaptureAppPlayback} plays the samples into media tracks, so they can be sent to OpenAI over WebRTC.
 * With speaker attribution, the app sends three channels: the mix, the microphone, and the system audio.
 *
 * Realtime reconnects reuse the tracks. Call `stop()` only when the complete listening session ends.
 */
export class TeammateAudioSourceController {
  /** Persistent connection to the local native capture app ("Linkando Teammate"), when available. */
  private captureWs: WebSocket | null = null
  /** Plays the capture app's audio into media tracks, while the capture app is the source. */
  private playback: CaptureAppPlayback | null = null
  /** Current mic stream owned by this controller. */
  private micStream: MediaStream | null = null
  /** True when the connected app can send separate channels. */
  private supportsSourceAudio = false
  /** Message handler of the probe WebSocket, restored after each capture. */
  private probeMessageHandler: ((event: MessageEvent) => void) | null = null
  /** Ends the wait for the app's format message when the WebSocket closes. */
  private cancelFormatWait: (() => void) | null = null
  /** Cleanup function for the capture-app availability probe WebSocket. */
  private stopProbe: (() => void) | null = null

  /**
   * Probe the native capture app and report whether it is connected.
   *
   * The probe WebSocket is kept open because the capture source reuses it for
   * audio frames if the user starts listening while the app is connected.
   */
  probeCaptureApp(onConnectedChange: (isConnected: boolean) => void) {
    this.stopProbe?.()

    try {
      const ws = new WebSocket(CAPTURE_APP_WS_URL)
      ws.binaryType = 'arraybuffer'

      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) ws.close()
      }, 3000)

      ws.onopen = () => {
        clearTimeout(connectionTimeout)
      }

      this.probeMessageHandler = event => {
        if (typeof event.data !== 'string') return
        const message = parseCaptureMessage(event.data)
        if (message?.type !== 'connected') return
        const capabilities: unknown[] = Array.isArray(message.capabilities) ? message.capabilities : []
        this.captureWs = ws
        this.supportsSourceAudio = capabilities.includes(SOURCE_AUDIO_CAPABILITY)
        onConnectedChange(true)
      }
      ws.onmessage = this.probeMessageHandler

      ws.onclose = () => {
        clearTimeout(connectionTimeout)
        this.cancelFormatWait?.()
        if (this.captureWs === ws) {
          this.captureWs = null
          this.supportsSourceAudio = false
          onConnectedChange(false)
        }
      }

      this.stopProbe = () => {
        clearTimeout(connectionTimeout)
        ws.onclose = null
        ws.onmessage = null
        ws.close()
        if (this.captureWs === ws) {
          this.captureWs = null
        }
      }
    } catch (err) {
      console.warn('Could not connect to capture app WebSocket:', err)
    }
  }

  /**
   * Start the best available audio input stream.
   *
   * Uses the capture app when its probe WebSocket is open; otherwise falls back
   * to direct microphone capture.
   * With `requestSpeakerChannels`, ask the app for speaker channels. The app sends them only when it captures
   * both the microphone and the system audio. Otherwise it sends the mixed audio, as older apps do.
   */
  async startPreferredSource(requestSpeakerChannels = false): Promise<TeammateAudioSourceResult> {
    if (this.captureWs?.readyState === WebSocket.OPEN) {
      const playback = await this.startCaptureAppSource(this.captureWs, requestSpeakerChannels && this.supportsSourceAudio)
      return { stream: playback.stream, speakerChannels: playback.speakerChannels, source: 'capture-app' }
    }

    return { stream: await this.startMicSource(), speakerChannels: [], source: 'mic' }
  }

  /**
   * Stop the active audio stream and any capture-app processing graph.
   *
   * This intentionally keeps the capture-app probe WebSocket open so a later
   * start can still use capture-app audio without probing again.
   */
  stop() {
    if (this.playback && this.captureWs?.readyState === WebSocket.OPEN) {
      this.captureWs.send(JSON.stringify({ type: 'stop-capture' }))
    }
    this.playback?.stop()
    this.playback = null

    this.micStream?.getTracks().forEach(track => track.stop())
    this.micStream = null

    if (this.captureWs) {
      // Keep handling app state messages between listening sessions.
      this.captureWs.onmessage = this.probeMessageHandler
    }
  }

  /**
   * Stop all audio resources and close the capture-app probe.
   */
  dispose() {
    this.stop()
    this.stopProbe?.()
    this.stopProbe = null
  }

  private async startMicSource() {
    // Ensure a previous capture graph or mic stream is not still feeding audio.
    this.stop()

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
    this.micStream = stream
    return stream
  }

  /**
   * Start capture-app audio on the probe WebSocket.
   * With `speakerChannels`, request separate channels, and wait for the app's format message,
   * which the app sends before any audio.
   */
  private async startCaptureAppSource(ws: WebSocket, speakerChannels: boolean) {
    // Reset any previous stream before reusing the probe WebSocket for audio.
    this.stop()

    // Start the audio graph before the app sends audio. Audio that arrives while it starts up would wait in the queue,
    // and delay all later audio.
    const playback = new CaptureAppPlayback()
    this.playback = playback
    // Without a speaker-channel request, the app sends mixed audio without a format message.
    // With the request, it first sends a format message: speaker channels, or mixed audio.
    let formatConfirmed = !speakerChannels
    let onFormat: ((format: Record<string, unknown>) => void) | null = null
    ws.onmessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        const message = parseCaptureMessage(event.data)
        if (message?.type === 'format') onFormat?.(message)
      } else if (event.data instanceof ArrayBuffer && formatConfirmed) {
        playback.push(event.data)
      }
    }
    const format = speakerChannels
      ? new Promise<void>((resolve, reject) => {
          const done = (error?: Error) => {
            clearTimeout(timeout)
            onFormat = this.cancelFormatWait = null
            if (error) reject(error)
            else resolve()
          }
          const timeout = setTimeout(
            () => done(new Error('The capture app did not describe its audio format.')),
            FORMAT_TIMEOUT_MS,
          )
          this.cancelFormatWait = () => done(new Error('The capture app closed the connection.'))
          onFormat = message => {
            const format = getCaptureFormat(message)
            if (format === 'speaker-channels') playback.useSpeakerChannels()
            done(format ? undefined : new Error('The capture app sent an unsupported audio format.'))
          }
        })
      : null
    ws.send(JSON.stringify({ type: 'start-capture', ...(speakerChannels && { sourceAudio: true }) }))
    if (format) {
      // The app sends the format before any audio. Separate channels start with the first audio frame.
      await format
      formatConfirmed = true
    }

    await playback.start()
    return playback
  }
}

/** Parse a text message from the capture app. Return null for a message that is not JSON. */
function parseCaptureMessage(data: string): Record<string, unknown> | null {
  try {
    return JSON.parse(data)
  } catch {
    return null
  }
}

/**
 * Read the format message of the capture app: 48 kHz, 32-bit float samples, interleaved by frame.
 * `speaker-channels`: three channels in the order mix, microphone, system audio.
 * `mixed`: the mix in both stereo channels. Return null for an unsupported format.
 */
function getCaptureFormat(format: { sampleRate?: unknown; encoding?: unknown; channels?: unknown }) {
  if (format.sampleRate !== CAPTURE_SAMPLE_RATE || format.encoding !== 'float32le' || !Array.isArray(format.channels)) {
    return null
  }
  const channels = format.channels.join()
  if (channels === 'live,mic,system') return 'speaker-channels'
  if (channels === 'live,live') return 'mixed'
  return null
}
